/** @format */
import { Injectable } from '@angular/core';
import { EntityState, EntityStore, QueryEntity, StoreConfig } from '@datorama/akita';
import { NgEntityServiceConfig } from '@datorama/akita-ng-entity-service';
import { OtmId, PersonGroup, PersonGroupDetails, PersonGroupDetailsForOwnGroup, SearchResult } from 'common-typescript/types';
import { Observable, throwError } from 'rxjs';
import { mono, Mono } from 'sis-common';
import { simpleObjectToQueryParams } from '../search-ng/search-utils';
import { EntityService } from './entity.service';

const CONFIG = {
    ENDPOINTS: {
        baseUrl: '/ori/api',
        getPersonGroupsForStudent() {
            return `${this.baseUrl}/person-groups/for-student`;
        },
        ownPersonGroupDetails: '/ori/api/person-group-details/own',
        getDetailsByIds() {
            return `${this.baseUrl}/person-group-details`;
        },
        searchGroups() {
            return `${this.baseUrl}/person-groups/search`;
        },
    },
};

export interface PersonGroupQueryParams {
    // from filters
    searchString?: string;
    personGroupType?: string;
    // additional parameters
    ignoreValidityPeriod: boolean;
    uiLang: string;
    // options
    start: number;
    limit: number;
}

@Injectable({
    providedIn: 'root',
})
@NgEntityServiceConfig({
    baseUrl: CONFIG.ENDPOINTS.baseUrl,
    resourceName: 'person-groups',
})
export class PersonGroupEntityService extends EntityService<PersonGroupState> {
    constructor() {
        super(PersonGroupStore, PersonGroupQuery);
    }

    getOwnPersonGroupDetails(): Observable<PersonGroupDetailsForOwnGroup[]> {
        return this.getHttp().get<PersonGroupDetailsForOwnGroup[]>(CONFIG.ENDPOINTS.ownPersonGroupDetails);
    }

    getDetailsByIds(personGroupIds: OtmId[], bypassStore: boolean = false, config = {}): Observable<PersonGroupDetails[]> {
        const options = { params: { id: personGroupIds.toString() } };
        return this.getHttp().get<PersonGroupDetails[]>(CONFIG.ENDPOINTS.getDetailsByIds(), options);
    }

    getPersonGroupsForStudent(studentId: OtmId): Observable<PersonGroup[]> {
        const url = `${CONFIG.ENDPOINTS.getPersonGroupsForStudent()}/${studentId}`;
        return this.getHttp().get<PersonGroup[]>(url);
    }

    searchGroups(queryParams: PersonGroupQueryParams): Mono<SearchResult<PersonGroup>> {
        if (!queryParams) {
            return mono(throwError(() => new Error('Cannot execute search without any parameters')));
        }
        const params = simpleObjectToQueryParams({ ...queryParams });
        return mono(this.getHttp().get<SearchResult<PersonGroup>>(CONFIG.ENDPOINTS.searchGroups(), { params }));
    }
}

type PersonGroupState = EntityState<PersonGroup, OtmId>;

@StoreConfig({ name: 'person-groups' })
class PersonGroupStore extends EntityStore<PersonGroupState> {}

class PersonGroupQuery extends QueryEntity<PersonGroupState> {
    constructor(protected store: PersonGroupStore) {
        super(store);
    }
}
